import React, { ReactNode, FC, useCallback, useEffect, useRef } from 'react';
import ActionButton, { ActionButtonVariant } from '@/pages/table/components/ActionButton/ActionButton';
import BetSlider, { HandPhases } from '@/pages/table/components/BetSlider/BetSlider';
import useIsMobileView from '@/hooks/useIsMobileView';
import { useSelector } from 'react-redux';
import { ReducerState } from '@/services/common/lib/reducerState';
import { twMerge } from 'tailwind-merge';
import CurrencyLabel from '@/pages/table/components/CurrencyLabel/CurrencyLabel';

interface RaiseActionButtonProps {
  canFold: boolean;
  onClick: () => void;
  betOrRaiseAmount: number | undefined;
  currentBet: number | undefined;
  max: number | undefined;
  pot: number | undefined;
  min: number | undefined;
  bigBlindValue: number | undefined;
  handPhase: HandPhases;
  showAllIn: boolean;
  onChange: (value: number) => void;
  showBetSlider: boolean;
  shouldShowBetSlider: boolean;
  shouldDisplayInBB: boolean;
  setShowBetSlider: ((newValue: boolean) => void) | undefined;
  smallBlindValue: number | undefined;
  isChecked: boolean;
  isPlayerTurn: boolean;
  canRaise?: boolean;
  className?: string;
}

const ButtonLabel = ({
  isPlayerTurn,
  canRaise,
  min,
  max,
  betOrRaiseAmount,
  bigBlindValue,
  shouldDisplayInBB,
}: {
  isPlayerTurn: boolean;
  showBetSlider: boolean;
  canRaise: boolean | undefined;
  min: number;
  max: number;
  betOrRaiseAmount: number | undefined;
  bigBlindValue: number | undefined;
  shouldDisplayInBB: boolean | undefined;
}): ReactNode | string => {
  const isMobileView = useIsMobileView();
  const isFixedLimited = min === max;

  if (!isPlayerTurn && isFixedLimited) return 'Raise Any';

  return (
    <span className={twMerge('flex flex-row items-center justify-center gap-0.5', isMobileView && 'flex-wrap')}>
      {canRaise ? 'Raise' : 'Bet'}
      {betOrRaiseAmount && (
        <CurrencyLabel
          amount={betOrRaiseAmount}
          bigBlindValue={bigBlindValue || 0}
          shouldDisplayInBB={!!shouldDisplayInBB}
          mobileCurrencyFormat={isMobileView}
        />
      )}
    </span>
  );
};

const RaiseActionButton: FC<RaiseActionButtonProps> = ({
  isChecked,
  isPlayerTurn,
  canFold,
  onClick,
  betOrRaiseAmount = 0,
  min = 0,
  max = 0,
  onChange,
  bigBlindValue = 0,
  smallBlindValue = 0,
  handPhase,
  pot = 0,
  currentBet = 0,
  showAllIn,
  showBetSlider,
  setShowBetSlider,
  shouldShowBetSlider,
  canRaise,
  className = '',
  shouldDisplayInBB,
}) => {
  const isMobileView = useIsMobileView({ checkForUserAgent: false });

  const betSliderRef = useRef<HTMLDivElement>(null);
  const user = useSelector((state: ReducerState) => state.auth);
  const betSliderStep = user.settings?.stepInBigBlindsEnabled ? bigBlindValue : smallBlindValue;

  const onClickRaiseHandler = () => {
    if (!isMobileView) {
      setShowBetSlider?.(false);
      onClick();
    } else {
      setShowBetSlider?.(true);
    }
  };

  const onClickConfirmHandler = () => {
    setShowBetSlider?.(false);
    onClick();
  };

  const showFullWidth = isMobileView && showBetSlider;

  const onClickOutsideHandler = useCallback(
    (event: MouseEvent) => {
      if (betSliderRef.current && !betSliderRef.current.contains(event.target as Node)) {
        setShowBetSlider?.(false);
      }
    },
    [setShowBetSlider],
  );

  useEffect(() => {
    if (showBetSlider) {
      document.addEventListener('mousedown', onClickOutsideHandler);
    } else {
      document.removeEventListener('mousedown', onClickOutsideHandler);
    }

    return () => {
      document.removeEventListener('mousedown', onClickOutsideHandler);
    };
  }, [onClickOutsideHandler, showBetSlider]);

  return (
    <div className={twMerge('relative w-full', className)}>
      <div ref={betSliderRef}>
        <div className={twMerge('relative z-[80]')}>
          <ActionButton
            variant={ActionButtonVariant.BetOrRaise}
            isChecked={isChecked}
            isPlayerTurn={isPlayerTurn}
            isDisabled={!canFold}
            fullWidth={showFullWidth}
            onClick={
              !shouldShowBetSlider ? onClickConfirmHandler : showBetSlider ? onClickConfirmHandler : onClickRaiseHandler
            }
            containerClassName="z-50"
            className={showBetSlider ? 'z-40 md:min-w-40' : ''}
          >
            <ButtonLabel
              isPlayerTurn={isPlayerTurn}
              showBetSlider={showBetSlider}
              canRaise={canRaise}
              min={min}
              max={max}
              betOrRaiseAmount={betOrRaiseAmount}
              bigBlindValue={bigBlindValue}
              shouldDisplayInBB={shouldDisplayInBB}
            />
          </ActionButton>
        </div>
        {showBetSlider && isMobileView && min !== max && (
          <BetSlider
            value={betOrRaiseAmount}
            min={min}
            max={max}
            onChange={onChange}
            bigBlindValue={bigBlindValue}
            handPhase={handPhase}
            pot={pot}
            currentBet={currentBet}
            showAllIn={showAllIn}
            setShowBetSlider={setShowBetSlider}
            isPlayerTurn={isPlayerTurn}
            step={betSliderStep}
            containerClassName="absolute left-1/2 -translate-x-1/2 top-1/2 z-30 -translate-y-full"
          />
        )}
      </div>
    </div>
  );
};

export default RaiseActionButton;
